/**
 * Details!-style meter window: a thin header (mode name, Current / Overall, tabs, collapse), then
 * one slim bar per party member in the class colour with "total (per second)". 'Current' resets at
 * every pull and keeps the last fight up between pulls. Rows are pooled; `update` writes only what
 * changed (called ~4×/s, never per frame).
 */
import type { DeepReadonly, World } from '@keystone/sim';
import { METER_MODES, type MeterMode, type MeterRow, type MeterScope } from '../game/meters';
import { el, onPress, setClass, setStyle, setText } from './dom';
import { shortNumber } from './format';
import { SCALE_X, barIndex, intText } from './kit/tokens';

const LABEL: Record<MeterMode, string> = { damage: 'Damage', healing: 'Healing', taken: 'Taken', interrupts: 'Kicks' };
const TITLE: Record<MeterMode, string> = { damage: 'Damage Done', healing: 'Healing Done', taken: 'Damage Taken', interrupts: 'Interrupts' };

interface RowEls {
  root: HTMLElement;
  fill: HTMLElement;
  rank: HTMLElement;
  name: HTMLElement;
  value: HTMLElement;
}

export class MeterPanel {
  readonly root: HTMLElement;
  mode: MeterMode = 'damage';
  /** Current fight (default; resets each pull) or the whole run. */
  scope: MeterScope = 'current';
  private readonly scopeBtn: HTMLButtonElement;
  private shownScope = '';
  /** Header collapse button (null when built without one). */
  readonly collapseBtn: HTMLButtonElement | null;
  private readonly tabs = new Map<MeterMode, HTMLElement>();
  private readonly title: HTMLElement;
  private readonly list: HTMLElement;
  private readonly rows: RowEls[] = [];
  private shownMode: MeterMode | null = null;

  constructor(parent: HTMLElement, cls = '', opts: { collapsible?: boolean; onCollapse?: () => void } = {}) {
    this.root = el('div', `panel meters ${cls}`.trim(), parent);
    const head = el('div', 'meter-head', this.root);
    this.title = el('div', 'meter-head-title', head);
    this.scopeBtn = el('button', 'meter-scope', head);
    this.scopeBtn.type = 'button';
    this.scopeBtn.tabIndex = -1;
    this.scopeBtn.title = 'Switch between the current fight and the whole run';
    onPress(this.scopeBtn, () => {
      this.scope = this.scope === 'current' ? 'overall' : 'current';
    });
    const tabs = el('div', 'meter-tabs', head);
    for (const m of METER_MODES) {
      const t = el('button', 'meter-tab', tabs, LABEL[m]);
      t.type = 'button';
      t.tabIndex = -1;
      t.title = TITLE[m];
      onPress(t, () => {
        this.mode = m;
        this.syncMode();
      });
      this.tabs.set(m, t);
    }
    this.collapseBtn = null;
    if (opts.collapsible) {
      const b = (this.collapseBtn = el('button', 'meter-collapse', head));
      b.type = 'button';
      b.tabIndex = -1;
      b.setAttribute('aria-label', 'Collapse meters');
      if (opts.onCollapse) onPress(b, opts.onCollapse);
    }
    this.list = el('div', 'meter-list', this.root);
    this.syncMode();
  }

  private syncMode(): void {
    if (this.shownMode === this.mode) return;
    this.shownMode = this.mode;
    for (const [m, t] of this.tabs) setClass(t, 'active', m === this.mode);
    setText(this.title, TITLE[this.mode]);
  }

  /** Header label for the scope: "Current" in combat, "Last fight" between pulls, "Overall". */
  scopeLabel(world: DeepReadonly<World>): string {
    if (this.scope === 'overall') return 'Overall';
    return world.partyIds.some((id) => world.units[id]?.inCombat) ? 'Current' : 'Last fight';
  }

  update(rows: readonly MeterRow[], scopeText = ''): void {
    this.syncMode();
    if (scopeText !== this.shownScope) {
      this.shownScope = scopeText;
      setText(this.scopeBtn, scopeText);
      setClass(this.scopeBtn, 'overall', this.scope === 'overall');
    }
    const top = Math.max(1, rows[0]?.value ?? 1);
    while (this.rows.length < rows.length) {
      const root = el('div', 'meter-row', this.list);
      const fill = el('div', 'meter-fill', root);
      const rank = el('span', 'meter-rank', root);
      const name = el('div', 'meter-name', root);
      const value = el('div', 'meter-value', root);
      this.rows.push({ root, fill, rank, name, value });
    }
    for (let i = 0; i < this.rows.length; i++) {
      const r = this.rows[i]!;
      const row = rows[i];
      setClass(r.root, 'hidden', !row);
      if (!row) continue;
      setStyle(r.fill, 'transform', SCALE_X[barIndex(row.value / top)]!);
      setStyle(r.fill, '--meter-c', row.color);
      setText(r.rank, intText(i + 1));
      setText(r.name, row.name);
      setText(r.value, this.mode === 'interrupts' ? intText(row.value) : `${shortNumber(row.value)} (${shortNumber(row.perSec)})`);
    }
  }
}
